import React, { useEffect, useState } from 'react';
import AnnotationRow from './AnnotationRow';
import { reorderHandlers } from './reorder';
import WordAnnotateForm from './WordAnnotateForm';
import { saveTitleSubtitle } from './subtitleStore';
import { TITLE_ANCHOR } from '../../annotations/annotationStore';
import './SubtitlePanel.css';
import './MomentAnnotator.css';
import './AddPanel.css';
import './TitlePanel.css';

/**
 * TitlePanel — the video title's subtitle (its English meaning) and its
 * annotated words, shown to students for the whole video (Requirements 44,
 * 45). It sits under the title card while the video is expanded: the
 * player's expand button shows and hides it (MediaPlayer titleContent).
 *
 * Props:
 *   videoId, title, language, yasna, yasnaError
 *   titleSubtitle — the title's subtitle, or null; onTitleSubtitleSaved(subtitle)
 *   annotations — the title annotations (useVideoContent)
 *   onSaved(annotation), onUpdated(annotation), onDeleted(id)
 */
function TitlePanel({
  videoId, title, language, yasna, yasnaError, titleSubtitle, onTitleSubtitleSaved,
  annotations = [], onSaved, onUpdated, onDeleted,
}) {
  const saved = (titleSubtitle && titleSubtitle.englishText) || '';
  const [english, setEnglish] = useState(saved);
  const [status, setStatus] = useState('');

  // Another video, or its subtitle loaded: show what is saved.
  useEffect(() => { setEnglish(saved); setStatus(''); }, [videoId, saved]);

  const saveMeaning = async (e) => {
    e.preventDefault();
    if (!english.trim() || english.trim() === saved) return;
    setStatus('saving');
    try {
      onTitleSubtitleSaved && onTitleSubtitleSaved(await saveTitleSubtitle(videoId, title, english.trim()));
      setStatus('saved');
    } catch (err) {
      console.error('Error saving the title\'s subtitle:', err);
      setStatus('error');
    }
  };

  if (!videoId) return null;

  const titleMoves = reorderHandlers(annotations, onUpdated);

  return (
    <section className="title-panel" aria-label="Title subtitle and annotations">
      {title && <p className="title-panel-title">{title}</p>}

      <form className="title-panel-meaning" onSubmit={saveMeaning}>
        <label className="subtitle-form-label" htmlFor="title-english">Title in English</label>
        <div className="word-annotate-row">
          <input
            id="title-english"
            className="subtitle-form-input"
            type="text"
            placeholder="e.g. Almost adults 1 – Norman's dad"
            value={english}
            onChange={(e) => { setEnglish(e.target.value); setStatus(''); }}
          />
          <button type="submit" className="subtitle-form-save-btn word-annotate-btn"
            disabled={!english.trim() || english.trim() === saved || status === 'saving'}>
            {status === 'saving' ? 'Saving…' : 'Save'}
          </button>
        </div>
        {status === 'saved' && <p className="title-panel-status" role="status">Saved.</p>}
        {status === 'error' && <p className="subtitle-form-error">Failed to save. Please try again.</p>}
      </form>

      <WordAnnotateForm
        videoId={videoId}
        language={language}
        yasna={yasna}
        yasnaError={yasnaError}
        start={null}
        end={null}
        anchor={TITLE_ANCHOR}
        label="Annotate a word or phrase from the title"
        inputId="title-text"
        placeholder="e.g. presque"
        onSaved={onSaved}
      />

      {annotations.length > 0 && (
        <ul className="moment-annotator-list title-panel-list" aria-label="Title words">
          {annotations.map((a) => (
            <AnnotationRow
              reorder={annotations.length > 1 ? titleMoves[a.id] : undefined}
              key={a.id}
              annotation={a}
              untimed
              yasna={yasna}
              language={language}
              currentTime={0}
              onUpdated={onUpdated}
              onDeleted={onDeleted}
            />
          ))}
        </ul>
      )}
      <p className="title-panel-hint">Students see the title's meaning and words with the video, for its whole length.</p>
    </section>
  );
}

export default TitlePanel;
